import { Button } from "@/components/ui/button";
import { Plus, Save, FolderOpen, Menu } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ChatActionsProps {
  onNewChat: () => void;
  onSaveChat: () => Promise<void>;
  onToggleArchive: () => void;
  showArchive: boolean;
  messagesExist: boolean;
}

export function ChatActions({
  onNewChat,
  onSaveChat,
  onToggleArchive,
  showArchive,
  messagesExist,
}: ChatActionsProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleSaveChat = async () => {
    try {
      await onSaveChat();
      toast({
        title: "Chat lagret",
        description: "Chatten har blitt lagret og kan finnes i arkivet.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Feil ved lagring",
        description: "Kunne ikke lagre chatten. Vennligst prøv igjen.",
      });
    }
  };

  if (isMobile) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Åpne meny</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={onNewChat}>
              <Plus className="mr-2 h-4 w-4" />
              Ny chat
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onToggleArchive}>
              <FolderOpen className="mr-2 h-4 w-4" />
              {showArchive ? "Lukk arkiv" : "Arkiv"}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleSaveChat}
              disabled={!messagesExist}
            >
              <Save className="mr-2 h-4 w-4" />
              Lagre chat
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="default"
        size="default"
        className="items-center gap-2 min-w-[80px] whitespace-nowrap"
        onClick={onNewChat}
      >
        <Plus className="h-4 w-4" />
        Start ny chat
      </Button>
      <Button
        variant="outline"
        size="default"
        className="items-center gap-2 min-w-[80px] whitespace-nowrap"
        onClick={() => onToggleArchive()}
      >
        <FolderOpen className="h-4 w-4" />
        {showArchive ? "Lukk" : "Arkiv"}
      </Button>
      <Button
        variant="outline"
        size="default"
        className="items-center gap-2 min-w-[80px] whitespace-nowrap"
        onClick={handleSaveChat}
        disabled={!messagesExist}
      >
        <Save className="h-4 w-4" />
        Lagre chat
      </Button>
    </div>
  );
}